import { FlexWorkerRelation } from 'ogp/hooks/queryHooks/flexWorkers/useGetFlexWorkerProfile';
import { Table } from 'redesign';
import { EmptyTableMessage } from 'ogp/components/common/empty-table-message';
import { usePopupProfile } from 'ogp/components/views/planning/hooks';
import { ReactNode, useMemo } from 'react';
import { useGetApplicantColumnDefinition } from './components/helpers/use-get-applicant-column-definition';
import { getApplicantTableData } from './components/helpers/get-applicant-table-data';
import {
  ApplicantColumnDefinition,
  ApplicantsTableOptions,
} from './components/helpers/applicant-table.types';
import {
  ApplicantDetail,
  ShiftApplicantRowActions,
  ShiftDetailsApplicant,
} from './applicant-table.types';

type Props = {
  shiftDetails: ShiftDetailsApplicant;
  applicants: ApplicantDetail[];
  hideEmploymentTypeFields?: boolean;
  getActions: (
    application: ApplicantDetail,
    shiftData: string,
    options?: { employmentTypeMismatch?: boolean; isMultiDayShift?: boolean }
  ) => ReactNode[];
};

function ApplicantTable({
  shiftDetails,
  applicants,
  getActions,
  hideEmploymentTypeFields = false,
}: Props) {
  const columns = useGetApplicantColumnDefinition(shiftDetails.employmentType, false, {
    hideEmploymentTypeFields,
  });
  const tableData = useMemo(
    () => getApplicantTableData(applicants, shiftDetails, hideEmploymentTypeFields),
    [applicants, shiftDetails, hideEmploymentTypeFields]
  );
  const { open: openPopupProfile, Element: PopupProfileElement } = usePopupProfile();

  const onProfileClick = (flexWorkerId: string, relation: FlexWorkerRelation) => {
    openPopupProfile(flexWorkerId, relation);
  };

  return applicants.length === 0 ? (
    <EmptyTableMessage
      title="Er zijn nog geen aanmeldingen!"
      subtitle="Er hebben zich nog geen flexwerkers aangemeld voor deze shift."
    />
  ) : (
    <>
      {PopupProfileElement}
      <Table<
        ApplicantColumnDefinition<ShiftApplicantRowActions>,
        ApplicantsTableOptions<ShiftApplicantRowActions>
      >
        stickyHeader={true}
        columns={columns}
        data={tableData}
        onProfileClick={onProfileClick}
        getRowActions={(rowActions) =>
          getActions(rowActions.application, shiftDetails.startDate, {
            employmentTypeMismatch: rowActions.employmentTypeMismatch,
            isMultiDayShift: shiftDetails.isMultiDayShift,
          })
        }
      />
    </>
  );
}

export { ApplicantTable };
